import { useEffect, useMemo, useState } from 'react';
import { CallAPIPromise } from '../routeBilling/menu/dashboard/biiling/modal/comman';

const normalizeBoolean = (value, fallback = true) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return String(value).toLowerCase() !== 'false';
};

const normalizeAudience = (audience) => {
  if (Array.isArray(audience)) return audience.map((type) => String(type).toUpperCase());
  if (typeof audience === 'string') {
    try {
      const parsedAudience = JSON.parse(audience);
      if (Array.isArray(parsedAudience)) {
        return parsedAudience.map((type) => String(type).trim().toUpperCase()).filter(Boolean);
      }
    } catch {
      // Fall back to comma separated text below.
    }
  }
  return String(audience || '').split(',').map((type) => type.trim().toUpperCase()).filter(Boolean);
};

const normalizeVersionUpdate = (item = {}) => ({
  ...item,
  id: item.id || item._id || item.versionUpdateId,
  audience: normalizeAudience(item.audience),
  active: normalizeBoolean(item.active, true),
  showOnHome: normalizeBoolean(item.showOnHome, true),
  showNewBadge: normalizeBoolean(item.showNewBadge, true),
});

export const normalizeVersionUpdateList = (response) => {
  const data = response?.data;
  const list = data?.data || data?.response || data?.versionUpdates || data?.updates || data?.list || data?.result || data || [];
  if (Array.isArray(list)) return list.map(normalizeVersionUpdate);
  if (list && typeof list === 'object') {
    return Object.entries(list).map(([id, item]) => normalizeVersionUpdate({ id, ...item }));
  }
  return [];
};

export const versionUpdatePayload = (form) => ({
  title: form.title?.trim() || '',
  description: form.description?.trim() || '',
  startDate: form.startDate,
  displayDays: Math.max(1, Number(form.displayDays) || 1),
  audience: form.audience || [],
  targetPath: form.targetPath || '',
  active: Boolean(form.active),
  showOnHome: Boolean(form.showOnHome),
  showNewBadge: Boolean(form.showNewBadge),
});

export const listVersionUpdates = async () => {
  const response = await CallAPIPromise('/api/versionUpdate/listVersionUpdates', {});
  return normalizeVersionUpdateList(response);
};

export const getActiveVersionUpdates = async (role) => {
  const response = await CallAPIPromise('/api/versionUpdate/getActiveVersionUpdates', { role });
  return normalizeVersionUpdateList(response);
};

export const createVersionUpdate = (form) =>
  CallAPIPromise('/api/versionUpdate/createVersionUpdate', versionUpdatePayload(form));

export const updateVersionUpdate = (id, form) =>
  CallAPIPromise('/api/versionUpdate/updateVersionUpdate', { id, ...versionUpdatePayload(form) });

export const deleteVersionUpdate = (id) =>
  CallAPIPromise('/api/versionUpdate/deleteVersionUpdate', { id });

export const isAnnouncementLive = (announcement, userType, now = new Date()) => {
  if (!announcement?.active) return false;
  const role = String(userType || '').toUpperCase();
  if (announcement.audience?.length && !announcement.audience.includes(role)) return false;
  if (!announcement.startDate) return true;
  const startDateText = String(announcement.startDate);
  const start = new Date(startDateText.includes('T') ? startDateText : `${startDateText}T00:00:00`);
  if (Number.isNaN(start.getTime())) return true;
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, Number(announcement.displayDays) || 1));
  return now >= start && now < end;
};

export const useProductAnnouncements = (prefixOrUserType, maybeUserType) => {
  const userType = maybeUserType || prefixOrUserType;
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (!userType) return undefined;
    let cancelled = false;
    getActiveVersionUpdates(userType)
      .then((items) => {
        if (!cancelled) setAnnouncements(items);
      })
      .catch(() => {
        if (!cancelled) setAnnouncements([]);
      });
    return () => { cancelled = true; };
  }, [userType]);

  return useMemo(
    () => announcements.filter((item) => isAnnouncementLive(item, userType)),
    [announcements, userType]
  );
};
